import { useTranslation } from 'react-i18next';
import { useState, type FC } from 'react';
import {
  VOICE_CALLS_PER_PAGE,
  useGetVoiceCalls,
} from 'infrastructure/hooks/ccm';
import BaseTableNew from 'components/atoms/base-table/new';
import EmptyClinicalVoiceIcon from 'components/atoms/icons/emptyClinicalVoiceIcon';
import EmptyTablePlaceholder from 'components/atoms/base-empty-table-placeholder';
import { ModuleType } from 'infrastructure/enums';
import classNames from 'classnames';
import { useModule } from 'infrastructure/providers/module-provider';
import { openInNewTab } from 'utils';
import { useGetCallRecordLink } from 'infrastructure/hooks/calls/use-get-call-record-link';

import s from './styles.module.scss';
import { columns } from './columns';
import ModuleTabs from '../module-tabs';

import type { DateRange, SortState } from 'infrastructure/types';

interface VoiceCallsLoggedProps {
  patientGuid: string;
  module?: ModuleType;
  showTabs?: boolean;
  dateRange?: DateRange | [];
  isAdminPanel?: boolean;
}

const VoiceCallsLogged: FC<VoiceCallsLoggedProps> = ({
  patientGuid,
  module = ModuleType.CCM,
  showTabs,
  dateRange,
  isAdminPanel,
}) => {
  const [page, setPage] = useState(1);
  const [documentationTimeSort, setDocumentationTimeSort] =
    useState<SortState>('');
  const { module: moduleType, changeModule: setModuleType } = useModule(module);
  const [downloadedCallId, setDownloadedCallId] = useState<string>();

  const { calls, isLoading } = useGetVoiceCalls({
    patientGuid,
    documentationTimeSort,
    meta: { items: VOICE_CALLS_PER_PAGE, page },
    module: moduleType,
    dateRange,
  });
  const { getCallRecordLink, getCallRecordLinkLoading } =
    useGetCallRecordLink();
  const { t } = useTranslation();

  const onDownload = async (callId: string) => {
    setDownloadedCallId(callId);
    const url = await getCallRecordLink(
      { callId, patientGuid, module: moduleType },
      {
        onSuccess() {
          setDownloadedCallId(undefined);
        },
      },
    );
    if (url) {
      openInNewTab(url);
    }
  };

  return (
    <div
      className={classNames({
        [s['admin-panel-wrapper']]: isAdminPanel,
        [s.wrapper]: !isAdminPanel,
      })}
    >
      <div className={classNames({ [s['title-wrapper']]: showTabs })}>
        <h3
          className={classNames(s.title, {
            [s['admin-panel-title']]: isAdminPanel,
          })}
          data-cy="voice-calls-section"
        >
          {t('labels.voiceCallsLogged')}
        </h3>
        {showTabs && (
          <ModuleTabs
            activeKey={moduleType}
            className={s.tabs}
            onChange={(tab) => {
              setModuleType(tab.key);
              setPage(1);
              setDocumentationTimeSort('');
            }}
          />
        )}
      </div>
      {isAdminPanel && <div className={s.divider} />}
      <BaseTableNew
        key={moduleType}
        items={calls?.items ?? []}
        columns={columns({
          onDownload,
          downloadedCallId,
          downloadLoading: getCallRecordLinkLoading,
          documentationTimeSort,
        })}
        bordered
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: VOICE_CALLS_PER_PAGE,
          total: calls?.count ?? 0,
          position: ['bottomCenter'],
        }}
        onChangePage={(newPage) => {
          setPage(newPage);
        }}
        dataCy="voice-calls-table"
        emptyText={
          <EmptyTablePlaceholder
            icon={<EmptyClinicalVoiceIcon />}
            text={t('labels.noVoiceCallsLogged')}
          />
        }
        onSort={(field, order) => {
          setDocumentationTimeSort(order);
        }}
      />
    </div>
  );
};
export default VoiceCallsLogged;
